import { DriveStep } from 'driver.js';
import { KEYCLOAK_GROUPS } from '../models/user.model';

export interface TutorialStep extends DriveStep {
  /** Required user groups to see this tutorial step */
  requiredGroups?: string[];
  /** Route association for context-specific tutorials */
  route?: string;
  /** Target route for navigation during tutorial */
  targetRoute?: string;
}

export type TutorialStatus = 'completed' | 'cancelled' | 'in-progress' | 'not-started';

const TUTORIAL_CONSTANTS = {
  SLA_HOURS_MIN: 24,
  SLA_HOURS_MAX: 48,
  REFERRAL_VALIDITY_HOURS: 24
};

export const ROUTE_TUTORIALS: Record<string, TutorialStep[]> = {
    // ==================================================================================
    // DASHBOARD (Root)
    // ==================================================================================
    '/': [
      // ---------------- HOSPITAL STAFF DASHBOARD ----------------
      {
        popover: {
          title: '👋 Welcome to the ICS Referral Hub',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">This system is designed for a <strong>"First-Time Right"</strong> workflow. Our shared goal is to transition patients into care programs <strong>quickly and smoothly</strong>.</p>
              
              <div style="background: rgba(11, 115, 142, 0.1); padding: 12px; border-radius: 8px; margin-bottom: 12px;">
                <strong>💡 What This Means For You:</strong><br/>
                Complete referrals get accepted faster. The more clinical data you provide upfront, the less back-and-forth with the ICS team.
              </div>
              
              <p style="margin: 0; color: #6b7280; font-size: 13px;"><em>Typical timeline: Complete referrals are reviewed within ${TUTORIAL_CONSTANTS.SLA_HOURS_MIN}-${TUTORIAL_CONSTANTS.SLA_HOURS_MAX} hours.</em></p>
            </div>
          `,
          align: 'center'
        },
        route: '/',
        requiredGroups: [KEYCLOAK_GROUPS.HOSPITAL_USER]
      },
      {
        popover: {
          title: '📋 Understanding the Referral Workflow',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">Here's what happens after you submit a referral:</p>
              
              <ol style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 8px;"><strong>You Submit</strong> → Referral enters the ICS queue</li>
                <li style="margin-bottom: 8px;"><strong>ICS Reviews</strong> → Clinical team assesses eligibility</li>
                <li style="margin-bottom: 8px;"><strong>Decision Made</strong> → Accepted, Waitlisted, or Rejected</li>
                <li style="margin-bottom: 0;"><strong>You're Notified</strong> → Email + In-App notification</li>
              </ol>
              
              <p style="margin: 0; background: #fef3c7; padding: 8px 12px; border-radius: 6px; font-size: 13px;">
              <p style="margin: 0; background: #dbeafe; padding: 8px 12px; border-radius: 6px; font-size: 13px;">
                <strong>💡 Tip:</strong> Check the "Messages" tab daily for submitted referrals with ICS follow-up questions.
              </p>
            </div>
          `,
          align: 'center'
        },
        route: '/',
        requiredGroups: [KEYCLOAK_GROUPS.HOSPITAL_USER]
      },
      {
        element: 'app-create-button',
        popover: {
          title: '🆕 Starting a New Referral',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;"><strong>Before You Click:</strong> Have these ready to avoid interruptions:</p>
              
              <ul style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 6px;">✅ Patient's <strong>Health Card Number (HCN)</strong></li>
                <li style="margin-bottom: 6px;">✅ Patient's <strong>MRN</strong> (Medical Record Number)</li>
                <li style="margin-bottom: 6px;">✅ <strong>Discharge Summary</strong> or clinical documents</li>
                <li style="margin-bottom: 0;">✅ Referring <strong>Physician Name</strong></li>
              </ul>
              
              <p style="margin: 0; color: #6b7280; font-size: 13px;"><em>Tip: Upload documents first—our AI will help pre-fill the form!</em></p>
            </div>
          `,
          side: 'bottom',
          align: 'end'
        },
        route: '/',
        requiredGroups: [KEYCLOAK_GROUPS.HOSPITAL_USER]
      },
      {
        element: 'th[pSortableColumn="status"]',
        popover: {
          title: '🚦 Status Column — Your Action Dashboard',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">Each status tells you exactly what's happening:</p>
              
              <div style="margin-bottom: 8px;">
                <span style="display: inline-block; background: #dbeafe; color: #1e40af; padding: 2px 8px; border-radius: 4px; font-weight: 600; font-size: 12px;">Drafted</span>
                <span style="color: #6b7280;"> — Work in progress. Auto-saved every few seconds.</span>
              </div>
              
              <div style="margin-bottom: 8px;">
                <span style="display: inline-block; background: #e0e7ff; color: #3730a3; padding: 2px 8px; border-radius: 4px; font-weight: 600; font-size: 12px;">Submitted</span>
                <span style="color: #6b7280;"> — In queue for ICS review. Watch messages for any follow-up questions from the ICS team.</span>
              </div>
              
              <div style="margin-bottom: 8px;">
                <span style="display: inline-block; background: #d1fae5; color: #065f46; padding: 2px 8px; border-radius: 4px; font-weight: 600; font-size: 12px;">Accepted</span>
                <span style="color: #6b7280;"> — Patient accepted into program.</span>
              </div>

              <div style="margin-bottom: 0;">
                <span style="display: inline-block; background: #fce7f3; color: #9d174d; padding: 2px 8px; border-radius: 4px; font-weight: 600; font-size: 12px;">Waitlisted</span>
                <span style="color: #6b7280;"> — Accepted but awaiting capacity.</span>
              </div>
            </div>
          `,
          side: 'bottom',
          align: 'center'
        },
        route: '/',
        requiredGroups: [KEYCLOAK_GROUPS.HOSPITAL_USER]
      },
      {
        element: 'app-filter-button',
        popover: {
          title: '🔍 Filtering Your Referrals',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">Use filters to quickly find what needs attention:</p>
              
              <ul style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 6px;"><strong>Status Filter:</strong> Focus on "Submitted" items</li>
                <li style="margin-bottom: 6px;"><strong>Date Range:</strong> Find recent or aging referrals</li>
                <li style="margin-bottom: 0;"><strong>Program Type:</strong> View TCU vs Community referrals</li>
              </ul>
              
              <p style="margin: 0; color: #059669; font-size: 13px;"><strong>Pro Tip:</strong> Check the "Messages" tab on submitted referrals daily!</p>
            </div>
          `,
          side: 'bottom',
          align: 'end'
        },
        route: '/',
        requiredGroups: [KEYCLOAK_GROUPS.HOSPITAL_USER]
      },
      {
        popover: {
          title: '✅ Dashboard Tour Complete!',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;"><strong>Quick Reference Summary:</strong></p>
              
              <ul style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 6px;">📝 <strong>Create referrals</strong> using the button above</li>
                <li style="margin-bottom: 6px;">👁️ <strong>Monitor statuses</strong> — watch for updates and new messages</li>
                <li style="margin-bottom: 6px;">💬 <strong>Check messages</strong> for ICS team communications</li>
                <li style="margin-bottom: 0;">🔔 <strong>Watch notifications</strong> for real-time updates</li>
              </ul>
              
              <p style="margin: 0; background: rgba(11, 115, 142, 0.1); padding: 8px 12px; border-radius: 6px; font-size: 13px;">
                <strong>Next:</strong> Click on any referral row to see detailed options and messaging.
              </p>
            </div>
          `,
          align: 'center'
        },
        route: '/',
        requiredGroups: [KEYCLOAK_GROUPS.HOSPITAL_USER]
      },

      // ---------------- ICS TEAM DASHBOARD ----------------
      {
        popover: {
          title: '🏥 Welcome to the ICS Intake Command Center',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">Your mission: <strong>Efficient Triage & Prioritization</strong> to ensure timely patient transitions based on clinical urgency and capacity.</p>
              
              <div style="background: rgba(11, 115, 142, 0.1); padding: 12px; border-radius: 8px; margin-bottom: 12px;">
                <strong>📊 Your Key Metrics:</strong><br/>
                • Average review time<br/>
                • First-contact resolution rate<br/>
                • Aging referrals (>48 hours)
              </div>
              
              <p style="margin: 0; color: #6b7280; font-size: 13px;"><em>Goal: Process new referrals within 24-48 hours of submission.</em></p>
            </div>
          `,
          align: 'center'
        },
        route: '/',
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_USER, KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      },
      {
        popover: {
          title: '🔄 Understanding Your Workflow Outcomes',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">When reviewing a referral, you set one of four outcomes:</p>
              
              <div style="margin-bottom: 8px;">
                <span style="display: inline-block; background: #d1fae5; color: #065f46; padding: 2px 8px; border-radius: 4px; font-weight: 600; font-size: 12px;">Accepted</span>
                <span style="color: #6b7280;"> — Patient meets criteria; capacity available</span>
              </div>
              
              <div style="margin-bottom: 8px;">
                <span style="display: inline-block; background: #fce7f3; color: #9d174d; padding: 2px 8px; border-radius: 4px; font-weight: 600; font-size: 12px;">Waitlisted</span>
                <span style="color: #6b7280;"> — Eligible but awaiting bed availability</span>
              </div>
              
              <div style="margin-bottom: 0;">
                <span style="display: inline-block; background: #fee2e2; color: #991b1b; padding: 2px 8px; border-radius: 4px; font-weight: 600; font-size: 12px;">Rejected</span>
                <span style="color: #6b7280;"> — Does not meet clinical criteria (requires reason)</span>
              </div>
            </div>
          `,
          align: 'center'
        },
        route: '/',
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_USER, KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      },
      {
        element: 'app-filter-button',
        popover: {
          title: '🎯 Queue Segmentation Strategy',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;"><strong>Manage volume effectively:</strong></p>
              
              <ul style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 6px;"><strong>By Status:</strong> Focus on your "Submitted" queue</li>
                <li style="margin-bottom: 6px;"><strong>By Program:</strong> Match filters to your current bed availability</li>
                <li style="margin-bottom: 6px;"><strong>By Hospital:</strong> Prioritize high-volume partners</li>
                <li style="margin-bottom: 0;"><strong>By Date:</strong> Identify aging referrals exceeding SLA</li>
              </ul>
              
              <p style="margin: 0; background: #fef3c7; padding: 8px 12px; border-radius: 6px; font-size: 13px;">
                <strong>⚠️ Watch for:</strong> Previously rejected patients resubmitting—may indicate condition changes.
              </p>
            </div>
          `,
          side: 'bottom',
          align: 'end'
        },
        route: '/',
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_USER, KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      },
      {
        element: 'th[pSortableColumn="referredDate"]',
        popover: {
          title: '⏱️ SLA Monitoring & Prioritization',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">Sort by <strong>Referred Date</strong> to manage aging referrals:</p>
              
              <div style="margin-bottom: 8px;">
                <span style="color: #059669; font-weight: 600;">< 24 hours:</span>
                <span style="color: #6b7280;"> On track for timely review</span>
              </div>
              
              <div style="margin-bottom: 8px;">
                <span style="color: #d97706; font-weight: 600;">24-48 hours:</span>
                <span style="color: #6b7280;"> Approaching SLA limit—prioritize</span>
              </div>
              
              <div style="margin-bottom: 12px;">
                <span style="color: #dc2626; font-weight: 600;">> 48 hours:</span>
                <span style="color: #6b7280;"> Exceeding target—immediate action needed</span>
              </div>
              
              <p style="margin: 0; color: #059669; font-size: 13px;"><strong>Pro Tip:</strong> Use descending sort to see oldest referrals first.</p>
            </div>
          `,
          side: 'bottom',
          align: 'center'
        },
        route: '/',
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_USER, KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      },
      {
        popover: {
          title: '✅ ICS Dashboard Tour Complete!',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;"><strong>Your Daily Workflow Summary:</strong></p>
              
              <ol style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 6px;">Check for aging referrals (>24 hours)</li>
                <li style="margin-bottom: 6px;">Review new "Submitted" referrals</li>
                <li style="margin-bottom: 6px;">Review recent submissions and send messages for missing data</li>
                <li style="margin-bottom: 0;">Update waitlist as capacity becomes available</li>
              </ol>
              
              <p style="margin: 0; background: rgba(11, 115, 142, 0.1); padding: 8px 12px; border-radius: 6px; font-size: 13px;">
                <strong>Next:</strong> Click any referral to see clinical details and make decisions.
              </p>
            </div>
          `,
          align: 'center'
        },
        route: '/',
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_USER, KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      }
    ],

    // ==================================================================================
    // REFERRAL CREATION (Hospital Staff Only)
    // ==================================================================================
    '/referrals/create': [
      {
        popover: {
          title: '📝 Creating a New Patient Referral',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">You're about to create a referral that will be reviewed by an ICS Coordinator. Here's what to expect:</p>
              
              <ol style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 6px;"><strong>Upload Documents</strong> → AI extracts key data</li>
                <li style="margin-bottom: 6px;"><strong>Review & Edit</strong> → Verify extracted information</li>
                <li style="margin-bottom: 6px;"><strong>Complete Form</strong> → Fill remaining fields</li>
                <li style="margin-bottom: 0;"><strong>Submit</strong> → Enters ICS review queue</li>
              </ol>
              
              <p style="margin: 0; background: rgba(11, 115, 142, 0.1); padding: 8px 12px; border-radius: 6px; font-size: 13px;">
                <strong>💡 Success Tip:</strong> Complete referrals are accepted 3x faster than incomplete ones.
              </p>
            </div>
          `,
          align: 'center'
        },
        route: '/referrals/create',
        requiredGroups: [KEYCLOAK_GROUPS.HOSPITAL_USER]
      },
      {
        element: '[data-tutorial="program-selection"]',
        popover: {
          title: '🎯 Selecting the Right Care Pathway',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;"><strong>This is the most critical decision!</strong> Choosing wrong = rejection.</p>
              
              <div style="margin-bottom: 10px; padding: 10px; background: #ede9fe; border-radius: 6px;">
                <strong style="color: #6d28d9;">TCU (Transitional Care Unit)</strong><br/>
                <span style="color: #6b7280; font-size: 13px;">Short-term rehabilitation. Goal: return home within 16 weeks. Best for: post-surgical recovery, stroke rehab, mobility restoration.</span>
              </div>
              
              <div style="margin-bottom: 12px; padding: 10px; background: #d1fae5; border-radius: 6px;">
                <strong style="color: #047857;">Community Care</strong><br/>
                <span style="color: #6b7280; font-size: 13px;">Long-term maintenance. Goal: ongoing support at home. Best for: chronic conditions, palliative care, ongoing nursing needs.</span>
              </div>
              
              <p style="margin: 0; color: #dc2626; font-size: 13px;"><strong>⚠️ Warning:</strong> Wrong pathway selection is the #1 cause of referral rejection.</p>
            </div>
          `,
          side: 'right',
          align: 'start'
        },
        route: '/referrals/create',
        requiredGroups: [KEYCLOAK_GROUPS.HOSPITAL_USER]
      },
      {
        element: '[data-tutorial="document-upload"]',
        popover: {
          title: '📄 Document Upload & AI Extraction',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;"><strong>Recommended Documents to Upload:</strong></p>
              
              <ul style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 4px;">✅ Discharge Summary</li>
                <li style="margin-bottom: 4px;">✅ Most Recent Assessment (PT/OT)</li>
                <li style="margin-bottom: 4px;">✅ Medication List</li>
                <li style="margin-bottom: 0;">✅ Lab Results (if relevant)</li>
              </ul>
              
              <div style="background: #fef3c7; padding: 8px 12px; border-radius: 6px; margin-bottom: 8px;">
                <strong>🤖 AI Extraction:</strong> Our system will scan documents and pre-fill form fields. <strong>You must verify all extracted data!</strong>
              </div>
              
              <p style="margin: 0; color: #6b7280; font-size: 13px;"><em>Supported formats: PDF, JPG, PNG (max 10MB each)</em></p>
            </div>
          `,
          side: 'left',
          align: 'start'
        },
        route: '/referrals/create',
        requiredGroups: [KEYCLOAK_GROUPS.HOSPITAL_USER]
      },
      {
        element: '[data-tutorial="step-progress"]',
        popover: {
          title: '📊 Form Progress & Auto-Save',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">The form tracks your progress and saves automatically:</p>
              
              <ul style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 6px;"><strong>Auto-Save:</strong> Every field saves as you type—no data lost</li>
                <li style="margin-bottom: 6px;"><strong>Drafted Status:</strong> Close and return anytime—your work is preserved</li>
                <li style="margin-bottom: 6px;"><strong>Required Fields:</strong> Marked with asterisk (*)</li>
                <li style="margin-bottom: 0;"><strong>Optional Fields:</strong> Fill these for faster approval!</li>
              </ul>
              
              <p style="margin: 0; background: #d1fae5; padding: 8px 12px; border-radius: 6px; font-size: 13px;">
                <strong>Pro Tip:</strong> More data = fewer questions from ICS = faster approval.
              </p>
            </div>
          `,
          side: 'bottom',
          align: 'center'
        },
        route: '/referrals/create',
        requiredGroups: [KEYCLOAK_GROUPS.HOSPITAL_USER]
      },
      {
        popover: {
          title: '✅ Ready to Build Your Referral!',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;"><strong>Quick Checklist Before Submitting:</strong></p>
              
              <ul style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 4px;">☑️ Correct program pathway selected</li>
                <li style="margin-bottom: 4px;">☑️ Patient demographics verified (HCN, MRN)</li>
                <li style="margin-bottom: 4px;">☑️ Clinical documents uploaded</li>
                <li style="margin-bottom: 4px;">☑️ AI-extracted data reviewed</li>
                <li style="margin-bottom: 0;">☑️ All required fields completed</li>
              </ul>
              
              <p style="margin: 0; color: #6b7280; font-size: 13px;"><em>After submission, monitor the dashboard for status updates and ICS messages.</em></p>
            </div>
          `,
          align: 'center'
        },
        route: '/referrals/create',
        requiredGroups: [KEYCLOAK_GROUPS.HOSPITAL_USER]
      }
    ],

    // ==================================================================================
    // AI EXTRACTION REVIEW (Hospital Staff Only)
    // ==================================================================================
    '/referrals/create/upload-documents': [
      {
        element: '[data-tutorial="extraction-results"]',
        popover: {
          title: 'AI Validation Required',
          description: '<strong>Critical Step:</strong> Verify extracted data against your documents. AI is an assistant, not a replacement for clinical review. You remain responsible for the accuracy of the referral.',
          side: 'bottom',
          align: 'center'
        },
        route: '/referrals/create/upload-documents',
        requiredGroups: [KEYCLOAK_GROUPS.HOSPITAL_USER]
      },
      {
        element: '[data-tutorial="confidence-badge"]',
        popover: {
          title: 'Confidence Scores',
          description: '<strong>Interpret the Signals:</strong><br/>• <span style="color: #0b738e; font-weight: bold;">High:</span> Likely correct.<br/>• <span style="color: #d97706; font-weight: bold;">Low:</span> Needs manual check.<br/><em>Always verify!</em>',
          side: 'left',
          align: 'start'
        },
        route: '/referrals/create/upload-documents',
        requiredGroups: [KEYCLOAK_GROUPS.HOSPITAL_USER]
      },
      {
        element: '[data-tutorial="accept-actions"]',
        popover: {
          title: 'Final Review',
          description: 'Once verified, click <strong>Accept</strong> to pre-fill the referral form. This saves significant data entry time while ensuring clinical accuracy.',
          side: 'top',
          align: 'end'
        },
        route: '/referrals/create/upload-documents',
        requiredGroups: [KEYCLOAK_GROUPS.HOSPITAL_USER]
      }
    ],

    // ==================================================================================
    // REFERRAL DETAILS (Shared but Role-Specific)
    // ==================================================================================
    '/referrals/:id': [
      // ---------------- HOSPITAL STAFF ----------------
      {
        element: '[data-tutorial="referral-header"]',
        popover: {
          title: '📊 Referral Status Overview',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">This header tells you everything at a glance:</p>
              <ul style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 6px;"><strong>Patient Name:</strong> Verify this matches your records</li>
                <li style="margin-bottom: 6px;"><strong>Hospital & Program:</strong> Confirm correct assignment</li>
                <li style="margin-bottom: 6px;"><strong>Referred Date:</strong> Track how long it's been in review</li>
                <li style="margin-bottom: 0;"><strong>Status Badge:</strong> Current workflow stage</li>
              </ul>
              <p style="margin: 0; background: #fef3c7; padding: 8px 12px; border-radius: 6px; font-size: 13px;">
                <strong>⚠️ Orange Status = Blocked!</strong> Check messages immediately for required information.
              </p>
            </div>
          `,
          side: 'bottom',
          align: 'start'
        },
        route: '/referrals/:id',
        requiredGroups: [KEYCLOAK_GROUPS.HOSPITAL_USER]
      },
      {
         element: '[data-tutorial="client-form-card"]',
         popover: {
           title: '📝 Client Referral Form',
           description: `
              <div style="line-height: 1.6;">
               <p style="margin-bottom: 12px;">This card shows a preview of the clinical questionnaire data you submitted. Please review to ensure all data is accurate before submission.</p>
               <div style="background: rgba(11, 115, 142, 0.1); padding: 12px; border-radius: 6px; margin-bottom: 12px;">
                 <strong style="color: #0b738e;">What's Inside:</strong>
                 <ul style="margin: 8px 0 0 20px; padding: 0;">
                   <li>Primary Diagnosis</li>
                   <li>Symptoms & history</li>
                   <li>Functional status</li>
                 </ul>
               </div>
             </div>
           `,
           side: 'top',
           align: 'start'
         },
         route: '/referrals/:id',
         requiredGroups: [KEYCLOAK_GROUPS.HOSPITAL_USER]
      },
      {
        element: '[data-tutorial="documents-tab"]',
        popover: {
          title: '📁 Managing Documents',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">This section contains all uploaded clinical documents:</p>
              <ul style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 6px;"><strong>View:</strong> Click any document to preview</li>
                <li style="margin-bottom: 6px;"><strong>Upload:</strong> Add additional documents anytime</li>
                <li style="margin-bottom: 6px;"><strong>Download All:</strong> Get a ZIP package of everything</li>
              </ul>
                <p style="margin: 0; color: #059669; font-size: 13px;"><strong>Tip:</strong> Upload supporting documents when responding to revision requests.</p>
            </div>
          `,
          side: 'right',
          align: 'start'
        },
        route: '/referrals/:id',
        requiredGroups: [KEYCLOAK_GROUPS.HOSPITAL_USER]
      },
      {
        element: '[data-tutorial="status-history-card"]',
        popover: {
          title: '📜 Status History Timeline',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">Track every milestone in the referral's journey:</p>
              <ul style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 6px;">When the referral was created</li>
                <li style="margin-bottom: 6px;">Each status change with timestamp</li>
                <li style="margin-bottom: 6px;">Who made each action (ICS coordinator name)</li>
                <li style="margin-bottom: 0;">Any notes or reasons provided</li>
              </ul>
              <p style="margin: 0; color: #6b7280; font-size: 13px;"><em>Use this to understand what's happened and what's expected next.</em></p>
            </div>
          `,
          side: 'left',
          align: 'start'
        },
        route: '/referrals/:id',
        requiredGroups: [KEYCLOAK_GROUPS.HOSPITAL_USER]
      },
      {
        element: '[data-tutorial="messages-tab"]',
        popover: {
          title: '💬 Secure Messaging with ICS Team',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;"><strong>How Communication Works:</strong></p>
              <ol style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 6px;">You send a message here</li>
                <li style="margin-bottom: 6px;">ICS Coordinator receives email notification</li>
                <li style="margin-bottom: 6px;">They reply through this same secure channel</li>
                <li style="margin-bottom: 0;">You get notified of their response</li>
              </ol>
              <div style="background: rgba(11, 115, 142, 0.1); padding: 8px 12px; border-radius: 6px; font-size: 13px;">
                <strong>🔒 PHI Protection:</strong> All messages stay within the Hub—no patient data in emails.
              </div>
            </div>
          `,
          side: 'right',
          align: 'start'
        },
        route: '/referrals/:id',
        requiredGroups: [KEYCLOAK_GROUPS.HOSPITAL_USER]
      },
      {
        element: '[data-tutorial="status-history-card"]',
        popover: {
          title: '📜 Status History Timeline',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">Track every milestone in the referral's journey:</p>
              
              <ul style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 6px;">When the referral was created</li>
                <li style="margin-bottom: 6px;">Each status change with timestamp</li>
                <li style="margin-bottom: 6px;">Who made each action (ICS coordinator name)</li>
                <li style="margin-bottom: 0;">Any notes or reasons provided</li>
              </ul>
              
              <p style="margin: 0; color: #6b7280; font-size: 13px;"><em>Use this to understand what's happened and what's expected next.</em></p>
            </div>
          `,
          side: 'left',
          align: 'start'
        },
        route: '/referrals/:id',
        requiredGroups: [KEYCLOAK_GROUPS.HOSPITAL_USER]
      },

      // ---------------- ICS TEAM ----------------
      {
        element: '[data-tutorial="referral-header"]',
        popover: {
          title: '🔍 Clinical Assessment View',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">Start your review by checking the header:</p>
              
              <ul style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 6px;"><strong>Program Match:</strong> Is this the right pathway for the patient?</li>
                <li style="margin-bottom: 6px;"><strong>Hospital:</strong> Note for any facility-specific considerations</li>
                <li style="margin-bottom: 6px;"><strong>Time in Queue:</strong> Check referred date vs. SLA</li>
              </ul>
              
              <p style="margin: 0; color: #6b7280; font-size: 13px;"><em>Verify the program selection before deep clinical review.</em></p>
            </div>
          `,
          side: 'bottom',
          align: 'start'
        },
        route: '/referrals/:id',
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_USER, KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      },
      {
        element: '[data-tutorial="documents-tab"]',
        popover: {
          title: '📋 Document Review & PHI Security',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">Review clinical documents to assess eligibility:</p>
              
              <ul style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 6px;"><strong>Discharge Summary:</strong> Primary diagnosis & prognosis</li>
                <li style="margin-bottom: 6px;"><strong>PT/OT Assessment:</strong> Mobility & ADL status</li>
                <li style="margin-bottom: 6px;"><strong>Medications:</strong> Complex care needs</li>
              </ul>
              
              <div style="background: #fee2e2; padding: 8px 12px; border-radius: 6px; font-size: 13px;">
                <strong>🔒 Security Reminder:</strong> Review in-app when possible. Avoid downloading PHI to local devices.
              </div>
            </div>
          `,
          side: 'right',
          align: 'start'
        },
        route: '/referrals/:id',
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_USER, KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      },
      {
        element: '[data-tutorial="status-history-card"]',
        popover: {
          title: '📊 Audit Trail & SLA Tracking',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">This timeline is your compliance record:</p>
              
              <ul style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 6px;"><strong>SLA Verification:</strong> Calculate time since submission</li>
                <li style="margin-bottom: 6px;"><strong>Handoff Tracking:</strong> See who touched the referral</li>
                <li style="margin-bottom: 6px;"><strong>Pattern Recognition:</strong> Identify recurring issues</li>
              </ul>
              
              <p style="margin: 0; color: #6b7280; font-size: 13px;"><em>All actions are logged and timestamped for compliance.</em></p>
            </div>
          `,
          side: 'left',
          align: 'start'
        },
        route: '/referrals/:id',
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_USER, KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      },
      {
        element: '[data-tutorial="status-actions"]',
        popover: {
          title: '⚖️ Making Your Decision',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;"><strong>Decision Framework:</strong></p>
              
              <div style="margin-bottom: 8px;">
                <span style="display: inline-block; background: #d1fae5; color: #065f46; padding: 2px 8px; border-radius: 4px; font-weight: 600; font-size: 12px;">Accepted</span>
                <span style="color: #6b7280;"> — Meets all clinical criteria, capacity exists</span>
              </div>
              
              <div style="margin-bottom: 8px;">
                <span style="display: inline-block; background: #fce7f3; color: #9d174d; padding: 2px 8px; border-radius: 4px; font-weight: 600; font-size: 12px;">Waitlisted</span>
                <span style="color: #6b7280;"> — Eligible but no current capacity</span>
              </div>
              
              <div style="margin-bottom: 12px;">
                <span style="display: inline-block; background: #fee2e2; color: #991b1b; padding: 2px 8px; border-radius: 4px; font-weight: 600; font-size: 12px;">Rejected</span>
                <span style="color: #6b7280;"> — Does not meet criteria (provide educational reason)</span>
              </div>
               
              <p style="margin: 0; color: #dc2626; font-size: 13px;"><strong>⚠️ Tip:</strong> Use messages to request missing information before rejecting an incomplete referral.</p>
            </div>
          `,
          side: 'left',
          align: 'start'
        },
        route: '/referrals/:id',
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_USER, KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      },
      {
        element: '[data-tutorial="messages-tab"]',
        popover: {
          title: '💬 Hospital Communication',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">Use secure messaging to clarify information:</p>
              
              <ul style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 6px;"><strong>Be Specific:</strong> Clearly state what's needed</li>
                <li style="margin-bottom: 6px;"><strong>Set Expectations:</strong> Mention response timeframe</li>
                <li style="margin-bottom: 6px;"><strong>Educational Tone:</strong> Help them improve future referrals</li>
              </ul>
              
              <p style="margin: 0; background: rgba(11, 115, 142, 0.1); padding: 8px 12px; border-radius: 6px; font-size: 13px;">
                <strong>Remember:</strong> Hospital staff receive email notification but reply here for PHI security.
              </p>
            </div>
          `,
          side: 'right',
          align: 'start'
        },
        route: '/referrals/:id',
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_USER, KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      },
      
      // ---------------- SHARED CONCLUSION ----------------
      {
        popover: {
          title: '✅ Referral Detail Tour Complete!',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;"><strong>Key Areas You've Learned:</strong></p>
              
              <ul style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 4px;">📊 Status interpretation</li>
                <li style="margin-bottom: 4px;">📁 Document management</li>
                <li style="margin-bottom: 4px;">💬 Secure messaging workflow</li>
                <li style="margin-bottom: 0;">📜 Audit trail tracking</li>
              </ul>
              
              <p style="margin: 0; background: rgba(11, 115, 142, 0.1); padding: 8px 12px; border-radius: 6px; font-size: 13px;">
                <strong>Best Practice:</strong> Keep all communication within the Hub for security and compliance.
              </p>
            </div>
          `,
          align: 'center'
        },
        route: '/referrals/:id'
      }
    ],

    // ==================================================================================
    // ADMIN FUNCTIONS (Admins Only)
    // ==================================================================================
    '/hospital-users': [
      {
        popover: {
          title: '👥 Managing Hospital Users',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">This is where you manage access for external hospital staff.</p>
              <ul style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 6px;"><strong>Invite Users:</strong> Send email invitations to new staff</li>
                <li style="margin-bottom: 6px;"><strong>Monitor Status:</strong> See who has accepted (Active) vs pending</li>
                <li style="margin-bottom: 0;"><strong>Manage Access:</strong> Deactivate users who no longer need access</li>
              </ul>
              <p style="margin: 0; background: #e0e7ff; padding: 8px 12px; border-radius: 6px; font-size: 13px;">
                <strong>💡 Note:</strong> Hospital users can only see referrals from their assigned hospital.
              </p>
            </div>
          `,
          align: 'center'
        },
        route: '/hospital-users',
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      },
      {
        element: '[data-tutorial="invite-user"]',
        popover: {
          title: '📧 Inviting New Users',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">Click here to invite a new user. You'll need:</p>
              <ul style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 4px;">First & Last Name</li>
                <li style="margin-bottom: 4px;">Email Address</li>
                <li style="margin-bottom: 0;">Hospital Assignment</li>
              </ul>
              <p style="margin: 0; color: #059669; font-size: 13px;"><strong>Pro Tip:</strong> They will receive an email with a setup link instantly.</p>
            </div>
          `,
          side: 'left',
          align: 'start'
        },
        route: '/hospital-users',
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      },
      {
        element: 'th:nth-child(4)', // Status column
        popover: {
          title: 'ying Invitation Status',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">Track user onboarding:</p>
              <div style="margin-bottom: 8px;">
                <i class="pi pi-envelope" style="color: #3b82f6"></i> <strong>Invited:</strong> Email sent, user hasn't logged in yet.
              </div>
              <div style="margin-bottom: 12px;">
                <i class="pi pi-check-circle" style="color: #0b738e"></i> <strong>Active:</strong> User has successfully set up their account.
              </div>
              <p style="margin: 0; color: #6b7280; font-size: 13px;"><em>Use the actions menu to <strong>Resend Invitation</strong> if needed.</em></p>
            </div>
          `,
          side: 'bottom',
          align: 'center'
        },
        route: '/hospital-users',
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      }
    ],

    // ==================================================================================
    // ADMIN: INTERNAL USERS
    // ==================================================================================
    '/internal-users': [
      {
        popover: {
          title: '🛡️ Managing the ICS Team',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">Manage accounts for your internal clinical and administrative team.</p>
              <div style="background: rgba(11, 115, 142, 0.1); padding: 12px; border-radius: 8px; margin-bottom: 12px;">
                <strong>Key Roles:</strong>
                <ul style="margin: 8px 0 0 20px; padding: 0;">
                  <li><strong>Internal User:</strong> Clinical reviewers (can approve/decline)</li>
                  <li><strong>Internal Admin:</strong> Can manage users and hospitals</li>
                  <li><strong>Super Admin:</strong> Full system configuration</li>
                </ul>
              </div>
            </div>
          `,
          align: 'center'
        },
        route: '/internal-users',
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      },
      {
        element: '[data-tutorial="add-internal-user"]',
        popover: {
          title: '➕ Adding Team Members',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">Add new internal staff here.</p>
              <p style="margin-bottom: 12px;"><strong>Program Specialization:</strong><br/>
              You can tag users with specific programs (e.g., "Cardiac", "Stroke") to help with work assignment.</p>
            </div>
          `,
          side: 'left',
          align: 'start'
        },
        route: '/internal-users',
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      }
    ],

    '/hospitals': [
      {
        popover: {
          title: '🏥 Hospital Configuration',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">This page controls which hospitals can submit referrals and to which programs.</p>
              
              <div style="background: rgba(11, 115, 142, 0.1); padding: 10px 12px; border-radius: 6px; margin-bottom: 12px;">
                <strong>📊 Key Settings:</strong><br/>
                • Hospital Name & ID<br/>
                • Eligible Programs<br/>
                • SSO Configuration (if applicable)<br/>
                • Active/Inactive Status
              </div>
              
              <p style="margin: 0; color: #6b7280; font-size: 13px;"><em>Hospital must be active before staff can submit referrals.</em></p>
            </div>
          `,
          align: 'center'
        },
        route: '/hospitals',
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      },
      {
        element: '[data-tutorial="add-hospital"]',
        popover: {
          title: '➕ Adding a New Hospital Partner',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;"><strong>Before Adding:</strong></p>
              
              <ul style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 6px;">✅ Partnership agreement is signed</li>
                <li style="margin-bottom: 6px;">✅ Program eligibility is confirmed</li>
                <li style="margin-bottom: 6px;">✅ Point of contact is identified</li>
                <li style="margin-bottom: 0;">✅ Training plan is in place</li>
              </ul>
              
              <p style="margin: 0; color: #059669; font-size: 13px;"><strong>Tip:</strong> Enable pilot programs before hospital staff try to submit.</p>
            </div>
          `,
          side: 'left',
          align: 'start'
        },
        route: '/hospitals',
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      }
    ],

    '/programs': [
      {
        popover: {
          title: '⚙️ The Programs Ecosystem',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">Programs are the heart of the referral system. This is where you connect clinical pathways to hospitals.</p>
              <div style="background: #f8fafc; padding: 12px; border-radius: 8px; border: 1px solid #e2e8f0; margin-bottom: 12px;">
                <strong style="color: #0f172a;">How it connects:</strong><br/>
                <span style="color: #64748b; font-size: 13px;">Create Program → Assign Hospitals → Hospitals see it in Referral Form</span>
              </div>
              <p style="margin: 0; color: #6b7280; font-size: 13px;"><em>Active programs appear immediately for assigned hospitals.</em></p>
            </div>
          `,
          align: 'center'
        },
        route: '/programs',
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      },
      {
        element: '[data-tutorial="create-program"]',
        popover: {
          title: '🆕 Creating a Program',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">Define a new care pathway:</p>
              <ul style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 6px;"><strong>Type:</strong> TCU (Rehab) or Community</li>
                <li style="margin-bottom: 6px;"><strong>Criteria:</strong> Who is eligible?</li>
                <li style="margin-bottom: 0;"><strong>Hospitals:</strong> Which partners can refer to this?</li>
              </ul>
            </div>
          `,
          side: 'left',
          align: 'start'
        },
        route: '/programs',
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      },
      {
        element: '[data-tutorial="filter-program"]',
        popover: {
          title: '🔍 Managing Programs',
          description: 'Use filters to find specific programs by type or hospital association. You can edit or archive programs using the card actions.',
          side: 'bottom',
          align: 'end'
        },
        route: '/programs',
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      }
    ]
};

export const GLOBAL_STEPS: TutorialStep[] = [
    {
      element: 'app-notification-dropdown',
      popover: {
        title: 'Real-Time Alerts',
        description: 'Stay updated on status changes and new messages. For ICS Teams, this includes new submissions; for Hospital Staff, it includes information requests.',
        side: 'bottom',
        align: 'end'
      }
    },
    {
      element: 'app-profile-dropdown',
      popover: {
        title: 'Account Governance',
        description: 'Manage your security settings and profile here. Remember to sign out when leaving a shared workstation.',
        side: 'bottom',
        align: 'end'
      }
    }
];

export const HOSPITAL_STAFF_FLOW: TutorialStep[] = [
      // Step 1: Welcome
      {
        popover: {
          title: '👋 Welcome to the ICS Referral Hub Tutorial!',
          description: `
            <div style="line-height: 1.6; max-width: 500px;">
              <p style="margin-bottom: 16px; font-size: 16px;">
                This <strong>8-minute tutorial</strong> will guide you through the process of uploading all documentation, creating a complete referral, and submitting your application.
              </p>
              <div style="background: rgba(11, 115, 142, 0.1); padding: 16px; border-radius: 8px; margin-bottom: 16px;">
                <strong style="color: #0b738e;">🎯 What You'll Learn:</strong>
                <ul style="margin: 8px 0 0 20px; padding: 0;">
                  <li>Understanding referral statuses</li>
                  <li>Creating a new referral</li>
                  <li>Tracking your referral's progress</li>
                  <li>Responding to information requests</li>
                </ul>
              </div>
              <p style="margin: 0; color: #6b7280; font-size: 14px;">
                <em>Typical timeline: Complete referrals are reviewed within ${TUTORIAL_CONSTANTS.SLA_HOURS_MIN}-${TUTORIAL_CONSTANTS.SLA_HOURS_MAX} hours.</em>
              </p>
            </div>
          `,
          align: 'center'
        },
        requiredGroups: [KEYCLOAK_GROUPS.HOSPITAL_USER]
      },
      // Step 2: Dashboard - Create Button
      {
        element: 'app-create-button',
        popover: {
          title: '🆕 Creating New Referrals',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">This button starts the referral creation process.</p>
              <p style="margin-bottom: 12px;"><strong>Before You Click:</strong></p>
              <ul style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 4px;">✅ Patient's Health Card Number</li>
                <li style="margin-bottom: 4px;">✅ Patient's MRN</li>
                <li style="margin-bottom: 0;">✅ Discharge Summary or clinical documents</li>
              </ul>
              <p style="margin: 0; color: #059669; font-size: 13px;"><strong>Next:</strong> We'll navigate to the create page in a moment...</p>
            </div>
          `,
          side: 'bottom',
          align: 'end'
        },
        targetRoute: '/',
        requiredGroups: [KEYCLOAK_GROUPS.HOSPITAL_USER]
      },
      // Step 3: Dashboard - Status Column
      {
        element: 'th[pSortableColumn="status"]',
        popover: {
          title: '🚦 Understanding Referral Status',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">Each status tells you what's happening:</p>
              <div style="margin-bottom: 8px;">
                <span style="display: inline-block; background: #dbeafe; color: #1e40af; padding: 2px 8px; border-radius: 4px; font-weight: 600; font-size: 12px;">Drafted</span>
                <span style="color: #6b7280;"> — Work in progress</span>
              </div>
              <div style="margin-bottom: 8px;">
                <span style="display: inline-block; background: #e0e7ff; color: #3730a3; padding: 2px 8px; border-radius: 4px; font-weight: 600; font-size: 12px;">Submitted</span>
                <span style="color: #6b7280;"> — In queue for review; check messages</span>
              </div>
              <div style="margin-bottom: 8px;">
                <span style="display: inline-block; background: #d1fae5; color: #065f46; padding: 2px 8px; border-radius: 4px; font-weight: 600; font-size: 12px;">Accepted</span>
                <span style="color: #6b7280;"> — Patient accepted</span>
              </div>
            </div>
          `,
          side: 'bottom',
          align: 'center'
        },
        targetRoute: '/',
        requiredGroups: [KEYCLOAK_GROUPS.HOSPITAL_USER]
      },
      // Step 4: Navigate to Sample Referral
      {
        popover: {
          title: '📋 Viewing Referral Details',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">Now let's look at a sample referral to see what the details look like.</p>
              <p style="margin: 0; color: #6b7280; font-size: 14px;">Navigating to the referral detail view...</p>
            </div>
          `,
          align: 'center'
        },
        targetRoute: '/referrals/tutorial/sample-referral',
        requiredGroups: [KEYCLOAK_GROUPS.HOSPITAL_USER]
      },

      // Step 5: Referral Overview (Header)
       {
        element: '[data-tutorial="referral-header"]',
        popover: {
          title: '📋 Your Referral Overview',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">This header tells you everything at a glance:</p>
              <ul style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 6px;"><strong>Patient Name:</strong> Verify this matches your records</li>
                <li style="margin-bottom: 6px;"><strong>Hospital & Program:</strong> Confirm correct assignment</li>
                <li style="margin-bottom: 6px;"><strong>Referred Date:</strong> Track how long it's been in review</li>
                <li style="margin-bottom: 0;"><strong>Status Badge:</strong> Current workflow stage</li>
              </ul>
            </div>
          `,
          side: 'bottom',
          align: 'start'
        },
        targetRoute: '/referrals/tutorial/sample-referral',
        requiredGroups: [KEYCLOAK_GROUPS.HOSPITAL_USER]
      },

      // Step 6: Client Referral Form (New)
      {
        element: '[data-tutorial="client-form-card"]',
        popover: {
          title: '📝 Client Referral Form',
          description: `
             <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">This card shows a preview of the clinical questionnaire data you submitted. Please review to ensure all data is accurate before submission.</p>
              <div style="background: rgba(11, 115, 142, 0.1); padding: 12px; border-radius: 6px; margin-bottom: 12px;">
                <strong style="color: #0b738e;">What's Inside:</strong>
                <ul style="margin: 8px 0 0 20px; padding: 0;">
                  <li>Primary Diagnosis</li>
                  <li>Symptoms & history</li>
                  <li>Functional status</li>
                </ul>
              </div>
            </div>
          `,
          side: 'top',
          align: 'start'
        },
        targetRoute: '/referrals/tutorial/sample-referral',
        requiredGroups: [KEYCLOAK_GROUPS.HOSPITAL_USER]
      },

      // Step 7: Documents (Existing but refined)
       {
        element: '[data-tutorial="documents-tab"]', // Using document tab as proxy for card if needed, or card itself
        popover: {
          title: '📁 Managing Documents',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">This section contains all uploaded clinical documents:</p>
              <ul style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 6px;"><strong>View:</strong> Click any document to preview</li>
                <li style="margin-bottom: 6px;"><strong>Upload:</strong> Add additional documents anytime</li>
                <li style="margin-bottom: 6px;"><strong>Download All:</strong> Get a ZIP package of everything</li>
              </ul>
            </div>
          `,
          side: 'left', // Adjusted side to avoid overlap
          align: 'start'
        },
        targetRoute: '/referrals/tutorial/sample-referral',
        requiredGroups: [KEYCLOAK_GROUPS.HOSPITAL_USER]
      },

      // Step 8: Status History (New)
      {
        element: '[data-tutorial="status-history-card"]',
        popover: {
          title: '📅 Tracking Status Changes',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">This is where you track the complete timeline of your referral's journey.</p>
              <ul style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 6px;"><strong>See every status change</strong> from Drafted to current state</li>
                <li style="margin-bottom: 6px;"><strong>View timestamps</strong> for each action</li>
                <li style="margin-bottom: 0;"><strong>Read notes</strong> from the ICS team</li>
              </ul>
            </div>
          `,
          side: 'left',
          align: 'start'
        },
        targetRoute: '/referrals/tutorial/sample-referral',
        requiredGroups: [KEYCLOAK_GROUPS.HOSPITAL_USER]
      },

      // Step 9: Messages (Refined)
      {
        element: '[data-tutorial="messages-tab"]',
        popover: {
          title: '💬 Secure Communication',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">This is where you can ask questions and communicate securely with the ICS team.</p>
              <div style="background: #d1fae5; padding: 12px; border-radius: 6px; margin-bottom: 12px;">
                <strong style="color: #065f46;">Best Practices:</strong>
                <ul style="margin: 8px 0 0 20px; padding: 0;">
                  <li>Respond to revision requests in the messages tab</li>
                  <li>Ask clarification questions</li>
                  <li>Keep all PHI within this secure chat</li>
                </ul>
              </div>
            </div>
          `,
          side: 'right',
          align: 'start'
        },
        targetRoute: '/referrals/tutorial/sample-referral',
        requiredGroups: [KEYCLOAK_GROUPS.HOSPITAL_USER]
      },

      // Step 10: Completion
      {
        popover: {
          title: '✅ Tutorial Complete!',
          description: `
            <div style="line-height: 1.6; max-width: 500px;">
              <p style="margin-bottom: 16px; font-size: 16px;">
                <strong>Congratulations!</strong> You've completed the tutorial.
              </p>
              <div style="background: #d1fae5; padding: 16px; border-radius: 8px; margin-bottom: 16px;">
                <strong style="color: #065f46;">🎯 Key Takeaways:</strong>
                <ul style="margin: 8px 0 0 20px; padding: 0;">
                  <li>Choose the right program (TCU vs Community)</li>
                  <li>Provide complete clinical information</li>
                  <li>Check messages for your referrals</li>
                  <li>Respond quickly to information requests</li>
                </ul>
              </div>
              <p style="margin: 0; font-size: 14px;">
                <strong>Need help later?</strong> Click the <strong>?</strong> button for page-specific guidance.
              </p>
            </div>
          `,
          align: 'center'
        },
        requiredGroups: [KEYCLOAK_GROUPS.HOSPITAL_USER]
      }
];

export function getICSTeamFlow(isAdmin: boolean): TutorialStep[] {
    const baseFlow: TutorialStep[] = [
      // Step 1: Welcome
      {
        popover: {
          title: '🏥 Welcome to the ICS Team Tutorial!',
          description: `
            <div style="line-height: 1.6; max-width: 500px;">
              <p style="margin-bottom: 16px; font-size: 16px;">
                This <strong>7-minute tutorial</strong> will show you how to efficiently triage referrals, ensure documentation is correct, and process applications.
              </p>
              <div style="background: rgba(11, 115, 142, 0.1); padding: 16px; border-radius: 8px; margin-bottom: 16px;">
                <strong style="color: #0b738e;">🎯 What You'll Learn:</strong>
                <ul style="margin: 8px 0 0 20px; padding: 0;">
                  <li>Prioritizing your referral queue</li>
                  <li>Reviewing clinical documentation</li>
                  <li>Making approval decisions</li>
                  <li>Communicating with hospital staff</li>
                </ul>
              </div>
              <p style="margin: 0; color: #6b7280; font-size: 14px;">
                <em>We'll use sample data so you can see exactly how everything works.</em>
              </p>
            </div>
          `,
          align: 'center'
        },
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_USER, KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      },
      // Step 2: Dashboard - Filter Button
      {
        element: 'app-filter-button',
        popover: {
          title: '🎯 Managing Your Queue',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">Use filters to prioritize effectively:</p>
              <ul style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 6px;"><strong>By Status:</strong> Focus on "Submitted" referrals, then review new message activity</li>
                <li style="margin-bottom: 6px;"><strong>By Date:</strong> Oldest referrals to meet SLA</li>
                <li style="margin-bottom: 0;"><strong>By Program:</strong> Match your bed availability</li>
              </ul>
              <p style="margin: 0; background: #fef3c7; padding: 8px 12px; border-radius: 6px; font-size: 13px;">
                <strong>⚠️ Watch for:</strong> Referrals >${TUTORIAL_CONSTANTS.SLA_HOURS_MIN} hours old need immediate attention.
              </p>
            </div>
          `,
          side: 'bottom',
          align: 'end'
        },
        targetRoute: '/',
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_USER, KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      },
      // Step 3: Dashboard - Referred Date
      {
        element: 'th[pSortableColumn="referredDate"]',
        popover: {
          title: '⏱️ SLA Monitoring',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">Sort by Referred Date to manage aging referrals:</p>
              <div style="margin-bottom: 8px;">
                <span style="color: #059669; font-weight: 600;">< ${TUTORIAL_CONSTANTS.SLA_HOURS_MIN} hours:</span>
                <span style="color: #6b7280;"> On track for timely review</span>
              </div>
              <div style="margin-bottom: 8px;">
                <span style="color: #d97706; font-weight: 600;">${TUTORIAL_CONSTANTS.SLA_HOURS_MIN}-${TUTORIAL_CONSTANTS.SLA_HOURS_MAX} hours:</span>
                <span style="color: #6b7280;"> Approaching SLA limit—prioritize</span>
              </div>
              <div style="margin-bottom: 12px;">
                <span style="color: #dc2626; font-weight: 600;">> ${TUTORIAL_CONSTANTS.SLA_HOURS_MAX} hours:</span>
                <span style="color: #6b7280;"> Exceeding target—immediate action</span>
              </div>
              <p style="margin: 0; color: #059669; font-size: 13px;"><strong>Goal:</strong> Process referrals within ${TUTORIAL_CONSTANTS.SLA_HOURS_MIN}-${TUTORIAL_CONSTANTS.SLA_HOURS_MAX} hours.</p>
            </div>
          `,
          side: 'bottom',
          align: 'center'
        },
        targetRoute: '/',
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_USER, KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      },
      // Step 4: Navigate to referral detail
      {
        popover: {
          title: '📋 Reviewing Referral Details',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">Now let's look at a sample referral to see the review process.</p>
              <p style="margin: 0; color: #6b7280; font-size: 14px;">Navigating to the referral detail view...</p>
            </div>
          `,
          align: 'center'
        },
        targetRoute: '/referrals/tutorial/sample-referral-ics',
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_USER, KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      },
      // Step 5: Referral Overview (Header)
       {
        element: '[data-tutorial="referral-header"]',
        popover: {
          title: '📋 Referral Overview',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">Quickly assess the referral context:</p>
              <ul style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 6px;"><strong>Patient:</strong> Confirm identity and demographics</li>
                <li style="margin-bottom: 6px;"><strong>Hospital:</strong> Check referring facility</li>
                <li style="margin-bottom: 6px;"><strong>Program:</strong> Verify if TCU or Community is appropriate</li>
                <li style="margin-bottom: 0;"><strong>Timeline:</strong> Monitor age against SLA</li>
              </ul>
            </div>
          `,
          side: 'bottom',
          align: 'start'
        },
        targetRoute: '/referrals/tutorial/sample-referral-ics',
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_USER, KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      },
      // Step 6: Client Referral Form
      {
        element: '[data-tutorial="client-form-card"]',
        popover: {
          title: '📝 Clinical Data Review',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">Review the structured clinical data submitted by the hospital:</p>
              <div style="background: rgba(11, 115, 142, 0.1); padding: 12px; border-radius: 6px; margin-bottom: 12px;">
                <strong style="color: #0b738e;">Key data points:</strong>
                <ul style="margin: 8px 0 0 20px; padding: 0;">
                  <li>Primary Diagnosis & History</li>
                  <li>Infection Control Status</li>
                  <li>Functional Requirements</li>
                </ul>
              </div>
            </div>
          `,
          side: 'top',
          align: 'start'
        },
        targetRoute: '/referrals/tutorial/sample-referral-ics',
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_USER, KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      },
      // Step 7: Referral Detail - Documents Tab
      {
        element: '[data-tutorial="documents-tab"]',
        popover: {
          title: '📁 Document Review',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">Review clinical documents to assess eligibility:</p>
              <ul style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 6px;"><strong>Discharge Summary:</strong> Primary diagnosis & prognosis</li>
                <li style="margin-bottom: 6px;"><strong>PT/OT Assessment:</strong> Mobility & ADL status</li>
                <li style="margin-bottom: 0;"><strong>Medications:</strong> Complex care needs</li>
              </ul>
              <p style="margin: 0; color: #059669; font-size: 13px;"><strong>Pro Tip:</strong> Verify program selection matches clinical needs.</p>
            </div>
          `,
          side: 'right',
          align: 'start'
        },
        targetRoute: '/referrals/tutorial/sample-referral-ics',
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_USER, KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      },
      // Step 8: Status History
      {
        element: '[data-tutorial="status-history-card"]',
        popover: {
          title: '📅 Audit Trail',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">Full visibility into the referral lifecycle:</p>
              <ul style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 6px;">Track all status transitions</li>
                <li style="margin-bottom: 6px;">View who made changes and when</li>
                <li style="margin-bottom: 0;">Review past notes and reasoning</li>
              </ul>
            </div>
          `,
          side: 'left',
          align: 'start'
        },
        targetRoute: '/referrals/tutorial/sample-referral-ics',
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_USER, KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      },
      // Step 9: Messages
      {
        element: '[data-tutorial="messages-tab"]',
        popover: {
          title: '💬 Hospital Communication',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">Direct line to the referring hospital staff:</p>
              <ul style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 6px;"><strong>Need More Information:</strong> Use messages to ask for missing docs or clarification</li>
                <li style="margin-bottom: 6px;"><strong>Status Updates:</strong> Explaining waitlist or decline reasons</li>
                <li style="margin-bottom: 0;"><strong>Coordination:</strong> Arranging admission logistics</li>
              </ul>
            </div>
          `,
          side: 'right',
          align: 'start'
        },
        targetRoute: '/referrals/tutorial/sample-referral-ics',
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_USER, KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      },
      // Step 10: Referral Detail - Status Actions
      {
        element: '[data-tutorial="status-actions"]',
        popover: {
          title: '⚖️ Making Your Decision',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;"><strong>Decision Framework:</strong></p>
              <div style="margin-bottom: 8px;">
                <span style="display: inline-block; background: #d1fae5; color: #065f46; padding: 2px 8px; border-radius: 4px; font-weight: 600; font-size: 12px;">Accepted</span>
                <span style="color: #6b7280;"> — Meets all clinical criteria, capacity exists</span>
              </div>
              <div style="margin-bottom: 8px;">
                <span style="display: inline-block; background: #fce7f3; color: #9d174d; padding: 2px 8px; border-radius: 4px; font-weight: 600; font-size: 12px;">Waitlisted</span>
                <span style="color: #6b7280;"> — Eligible but no current capacity</span>
              </div>
              <div style="margin-bottom: 8px;">
                <span style="display: inline-block; background: #e0e7ff; color: #3730a3; padding: 2px 8px; border-radius: 4px; font-weight: 600; font-size: 12px;">Submitted</span>
                <span style="color: #6b7280;"> — Keep the referral submitted while you ask for missing data in Messages</span>
              </div>
              <div style="margin-bottom: 12px;">
                <span style="display: inline-block; background: #fee2e2; color: #991b1b; padding: 2px 8px; border-radius: 4px; font-weight: 600; font-size: 12px;">Rejected</span>
                <span style="color: #6b7280;"> — Does not meet criteria</span>
              </div>
              <p style="margin: 0; color: #dc2626; font-size: 13px;"><strong>Tip:</strong> Keep incomplete referrals in "Submitted" and request the missing details in Messages.</p>
            </div>
          `,
          side: 'left',
          align: 'start'
        },
        targetRoute: '/referrals/tutorial/sample-referral-ics',
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_USER, KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      }
    ];

    // Admin-specific steps (shown only to admins)
    const adminSteps: TutorialStep[] = isAdmin ? [
      // Admin Step 1: Admin Dashboard intro (center popup, no navigation needed —
      // the /admin route redirects to /, so we stay on whatever page we're on;
      // the sidebar nav items highlighted in steps 2-5 are always visible)
      {
        popover: {
          title: '🛠️ Admin Dashboard',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">As an admin, you have additional responsibilities for system management.</p>
              <p style="margin: 0; color: #6b7280; font-size: 14px;">Let's review your administrative capabilities...</p>
            </div>
          `,
          align: 'center'
        },
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      },
      // Admin Step 2: Program Management
      {
        element: '[data-tutorial="programs-nav"]',
        popover: {
          title: '⚙️ Program Management',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">Configure care programs and their availability:</p>
              <ul style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 6px;"><strong>Create Programs:</strong> Define different types of programs</li>
                <li style="margin-bottom: 6px;"><strong>Sub-Programs:</strong> Assign specific sub-programs to main categories</li>
                <li style="margin-bottom: 6px;"><strong>Hospital Assignment:</strong> Select which hospitals can refer to each program</li>
                <li style="margin-bottom: 0;"><strong>Referral Targets:</strong> These are the programs under which referrals will be submitted</li>
              </ul>
              <p style="margin: 0; color: #059669; font-size: 13px;"><strong>Note:</strong> Hospitals can only submit referrals to programs they are assigned to.</p>
            </div>
          `,
          side: 'right',
          align: 'start'
        },
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      },
      // Admin Step 3: Hospital Management
      {
        element: '[data-tutorial="hospitals-nav"]',
        popover: {
          title: '🏥 Hospital Management',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">Manage hospital partnerships from this section:</p>
              <ul style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 6px;"><strong>Add New Hospitals:</strong> Onboard new partner facilities</li>
                <li style="margin-bottom: 6px;"><strong>Edit Details:</strong> Update contact info and settings</li>
                <li style="margin-bottom: 6px;"><strong>View Referrals:</strong> See all referrals from each hospital</li>
                <li style="margin-bottom: 0;"><strong>Monitor Status:</strong> Track hospital performance metrics</li>
              </ul>
              <p style="margin: 0; color: #059669; font-size: 13px;"><strong>Note:</strong> Changes here affect which hospitals can submit referrals.</p>
            </div>
          `,
          side: 'right',
          align: 'start'
        },
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      },
      // Admin Step 4: Hospital Users
      {
        element: '[data-tutorial="users-nav"]',
        popover: {
          title: '👥 Hospital Users',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">Manage hospital staff who can submit referrals:</p>
              <ul style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 6px;"><strong>Invite Users:</strong> Send invitations to hospital staff</li>
                <li style="margin-bottom: 6px;"><strong>Assign Hospitals:</strong> Link users to their facilities</li>
                <li style="margin-bottom: 6px;"><strong>Monitor Activity:</strong> Track user status and engagement</li>
                <li style="margin-bottom: 0;"><strong>Deactivate Access:</strong> Remove users when needed</li>
              </ul>
              <p style="margin: 0; color: #059669; font-size: 13px;"><strong>Tip:</strong> Users must accept their invitation before they can access the system.</p>
            </div>
          `,
          side: 'right',
          align: 'start'
        },
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      },
      // Admin Step 5: Internal Users
      {
        element: '[data-tutorial="internal-users-nav"]',
        popover: {
          title: '👨‍⚕️ Internal Users',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">Manage your ICS team and administrators:</p>
              <ul style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 6px;"><strong>ICS Team:</strong> Clinical reviewers who triage referrals</li>
                <li style="margin-bottom: 6px;"><strong>Admins:</strong> Full system access and user management</li>
                <li style="margin-bottom: 6px;"><strong>Super Admins:</strong> All permissions plus system configuration</li>
                <li style="margin-bottom: 0;"><strong>Role Assignment:</strong> Assign appropriate access levels</li>
              </ul>
              <p style="margin: 0; background: #fef3c7; padding: 8px 12px; border-radius: 6px; font-size: 13px;">
                <strong>⚠️ Important:</strong> Only grant admin access to users who need it for their role.
              </p>
            </div>
          `,
          side: 'right',
          align: 'start'
        },
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      },
      // Admin Step 6: Completion
      {
        popover: {
          title: '✅ Admin Sections Complete!',
          description: `
            <div style="line-height: 1.6;">
              <p style="margin-bottom: 12px;">You've reviewed all the key administrative sections:</p>
              <ul style="margin: 0 0 12px 0; padding-left: 20px;">
                <li style="margin-bottom: 6px;">⚙️ <strong>Programs</strong> - Configure care programs</li>
                <li style="margin-bottom: 6px;">🏥 <strong>Hospitals</strong> - Manage partner facilities</li>
                <li style="margin-bottom: 6px;">👥 <strong>Hospital Users</strong> - Invite hospital staff</li>
                <li style="margin-bottom: 6px;">👨‍⚕️ <strong>Internal Users</strong> - Manage ICS team</li>
              </ul>
              <p style="margin: 0; background: rgba(11, 115, 142, 0.1); padding: 12px; border-radius: 6px; font-size: 13px;">
                <strong>💡 Tip:</strong> Don't forget to check <strong>Settings → Notifications</strong> to customize your notification preferences.
              </p>
            </div>
          `,
          align: 'center'
        },
        requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
      }
    ] : [];

    // Combine base flow with admin-specific steps
    const combinedFlow = [...baseFlow, ...adminSteps];

    // Add completion step at the end
    combinedFlow.push({
      popover: {
        title: '✅ Tutorial Complete!',
        description: isAdmin ? `
          <div style="line-height: 1.6; max-width: 500px;">
            <p style="margin-bottom: 16px; font-size: 16px;">
              <strong>You're ready!</strong> You now understand both the ICS intake workflow and admin responsibilities.
            </p>
            <div style="background: #d1fae5; padding: 16px; border-radius: 8px; margin-bottom: 16px;">
              <strong style="color: #065f46;">🎯 Your Daily Workflow:</strong>
              <ol style="margin: 8px 0 0 20px; padding: 0;">
                <li>Check for priority referrals (>${TUTORIAL_CONSTANTS.SLA_HOURS_MIN}h)</li>
                <li>Review new "Submitted" referrals</li>
                <li>Monitor Submitted queue</li>
                <li>Update waitlist as capacity opens</li>
              </ol>
            </div>
            <div style="background: #ede9fe; padding: 16px; border-radius: 8px; margin-bottom: 16px;">
              <strong style="color: #6d28d9;">🛠️ Admin Tasks:</strong>
              <ul style="margin: 8px 0 0 20px; padding: 0;">
                <li>Onboard new hospitals and users</li>
                <li>Configure program capacity</li>
                <li>Monitor system performance</li>
              </ul>
            </div>
            <p style="margin: 0; font-size: 14px;">
              <strong>Need help later?</strong> Click the <strong>?</strong> button for page-specific guidance.
            </p>
          </div>
        ` : `
          <div style="line-height: 1.6; max-width: 500px;">
            <p style="margin-bottom: 16px; font-size: 16px;">
              <strong>You're ready!</strong> You now understand the ICS intake workflow.
            </p>
            <div style="background: #d1fae5; padding: 16px; border-radius: 8px; margin-bottom: 16px;">
              <strong style="color: #065f46;">🎯 Your Daily Workflow:</strong>
              <ol style="margin: 8px 0 0 20px; padding: 0;">
                <li>Check for priority referrals (>${TUTORIAL_CONSTANTS.SLA_HOURS_MIN}h)</li>
                <li>Review new "Submitted" referrals</li>
                <li>Monitor Submitted queue</li>
                <li>Update waitlist as capacity opens</li>
              </ol>
            </div>
            <p style="margin: 0; font-size: 14px;">
              <strong>Need help later?</strong> Click the <strong>?</strong> button for page-specific guidance.
            </p>
          </div>
        `,
        align: 'center'
      },
      requiredGroups: [KEYCLOAK_GROUPS.INTERNAL_USER, KEYCLOAK_GROUPS.INTERNAL_ADMIN, KEYCLOAK_GROUPS.SUPER_ADMIN]
    });

    return combinedFlow;
}
